import type { RecoCarouselContainer } from "../carousel/RecoCarouselContainer";
import { Placement, RenderRequestDto } from "../../_generated";
import RecoConsent, {
  RecoConsentInterface,
  RichRelevanceConsent,
} from "../RecoConsent";
import { recoCarouselContainerTagName } from "../carousel/recoCarouselContainerTagName";
import { textContentToJSON } from "../utils/textContentToJSON";
import AlgonomyCookieStringStorage from "../AlgonomyCookieStringStorage";

export type SSIDto = {
  countryUrlPrefix: string;
  useMock: boolean;
  aktionsFlagsEnabled: boolean;
  placement: Placement;
};

export abstract class RecoLoader extends HTMLElement {
  protected ssiDto: SSIDto | null | undefined;
  protected richRelevanceConsent: RichRelevanceConsent =
    false as RichRelevanceConsent;
  private recoCarouselContainer: RecoCarouselContainer | null | undefined;
  protected recoConsent: RecoConsentInterface;
  protected campAktionsflagsInitialized: CustomEvent = new CustomEvent(
    "camp:aktionsflags:initialized",
  );

  constructor() {
    super();
    this.recoConsent = new RecoConsent();
  }

  connectedCallback(): void {
    window.requestAnimationFrame(() => {
      // This is placed inside a request animation frame because the connected callback is called when the custom element
      // tags is in the DOM but not all child element might have been parsed yet.
      // See https://stackoverflow.com/a/69389780
      this.recoCarouselContainer = this.querySelector<RecoCarouselContainer>(
        recoCarouselContainerTagName,
      );
    });
    this.ssiDto = this.getSSIDto();
  }

  private getSSIDto(): SSIDto | null {
    const ssiDto = this.querySelector('[data-ssi-dto="ssi-dto"]')?.textContent;
    return textContentToJSON<SSIDto>(ssiDto);
  }

  protected getRequestBody(placements: Placement[]): RenderRequestDto {
    const algonomyCookieString = AlgonomyCookieStringStorage.retrieve();
    if (algonomyCookieString != null) {
      return {
        algonomyCookieString: algonomyCookieString,
        placements: placements,
        consent: this.richRelevanceConsent,
      };
    } else {
      return {
        placements: placements,
        consent: this.richRelevanceConsent,
      };
    }
  }

  protected renderRecommendation(recommendations: string): Promise<void> {
    return new Promise((resolve) => {
      const fragment = document
        .createRange()
        .createContextualFragment(recommendations);
      // https://developers.google.com/web/fundamentals/performance/rendering/optimize-javascript-execution#use_requestanimationframe_for_visual_changes
      window.requestAnimationFrame(() => {
        this.recoCarouselContainer?.replaceWith(fragment);
        // Replace the old (and now gone) reference
        this.recoCarouselContainer = this.querySelector<RecoCarouselContainer>(
          recoCarouselContainerTagName,
        );
        resolve();
      });
    });
  }
}
